/**
 * Analytics identity + environment.
 *
 * Three ids, three different lifetimes:
 *   anonymous_id       localStorage, survives reloads and tabs (the "person")
 *   browser_session_id sessionStorage, one per tab session (the "visit")
 *   event_id           per event, `crypto.randomUUID()`
 *
 * No email, user id, or any account identifier is stored or sent. The server
 * hashes `anonymous_id` with a secret salt; the raw value never reaches an
 * analytics table.
 *
 * Storage can throw or be absent (Safari private mode, disabled cookies, a
 * sandboxed iframe). Every accessor falls back to an in-memory value so
 * analytics degrades to "session-only" instead of breaking the page.
 */

const ANONYMOUS_ID_KEY = "ato_analytics_id_v1";
const SESSION_ID_KEY = "ato_analytics_session_id_v1";

/** Deployment this build reports as. */
export type AnalyticsEnvironment = "development" | "staging" | "production";

let memoryAnonymousId: string | null = null;
let memorySessionId: string | null = null;

/** UUID with a non-crypto fallback for older/embedded webviews. */
export function newId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  // Not security-sensitive: this id only de-duplicates rows server-side.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readStore(store: "local" | "session", key: string): string | null {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    return s?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStore(store: "local" | "session", key: string, value: string): void {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    s?.setItem(key, value);
  } catch {
    // Quota/permission errors are non-fatal — the in-memory value still works.
  }
}

/** Stable pseudonymous id for this browser. */
export function getAnonymousId(): string {
  const stored = readStore("local", ANONYMOUS_ID_KEY);
  if (stored) return stored;
  if (!memoryAnonymousId) memoryAnonymousId = newId();
  writeStore("local", ANONYMOUS_ID_KEY, memoryAnonymousId);
  return memoryAnonymousId;
}

/** Per-tab-session id. Used to join search → run within one visit. */
export function getBrowserSessionId(): string {
  const stored = readStore("session", SESSION_ID_KEY);
  if (stored) return stored;
  if (!memorySessionId) memorySessionId = newId();
  writeStore("session", SESSION_ID_KEY, memorySessionId);
  return memorySessionId;
}

/**
 * Environment is a BUILD-TIME fact, never inferred from the hostname: a
 * preview URL or a proxied host would otherwise be misreported, and the whole
 * contract depends on staging data never reaching production KPIs.
 *
 * An unrecognised value disables analytics rather than guessing production.
 */
export function resolveEnvironment(): AnalyticsEnvironment | null {
  const raw = import.meta.env.VITE_DEPLOY_ENV;
  if (raw === "development" || raw === "staging" || raw === "production") {
    return raw;
  }
  return null;
}

/** Build identifier (version or commit SHA) injected at build time. */
export function resolveAppVersion(): string | null {
  const v = import.meta.env.VITE_APP_VERSION;
  return typeof v === "string" && v.length > 0 ? v : null;
}

/** Test seam — clears the in-memory fallbacks. */
export function resetAnalyticsIdentityForTests(): void {
  memoryAnonymousId = null;
  memorySessionId = null;
}
